import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Flame } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { projectAPI } from '../services/api';
import { fmt, wishLabel } from '../hooks/usePlanningDiagnostics';
import { isStandardWorkday } from '../utils/workDays';

const levelStyle = {
  danger: 'bg-red-50 text-red-700 border-red-200',
  warn: 'bg-orange-50 text-orange-700 border-orange-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
};

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return format(new Date(y, m - 1, 1), 'MMMM yyyy', { locale: fr });
};
const shortDate = (d) => (d ? format(new Date(d), 'dd/MM', { locale: fr }) : '—');
const hoursFmt = (h) => `${Math.round(h).toLocaleString('fr-FR')} h`;

// Personnel à trouver pour faire, d'ici la fin du chantier, les heures que
// personne ne couvre : journées de 7 h sur les jours ouvrés, au moins une personne.
const HOURS_PER_DAY = 7;
const staffNeeded = (uncovered, project) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = project?.actualStartDate ? new Date(`${project.actualStartDate.slice(0, 10)}T00:00:00`) : today;
  const end = new Date(`${uncovered.end.slice(0, 10)}T00:00:00`);
  let days = 0;
  for (let d = new Date(start > today ? start : today); d <= end; d.setDate(d.getDate() + 1)) {
    if (isStandardWorkday(d)) days += 1;
  }
  return Math.max(1, Math.ceil(uncovered.hours / (Math.max(1, days) * HOURS_PER_DAY)));
};

// Tableau de bord de l'onglet Chantiers, réduit à une bande de quatre
// indicateurs : le détail de chacun (tableau de charge, formulaire de première
// disponibilité, retards, alertes) ne se déplie qu'à la demande. Avant, ces
// quatre panneaux s'empilaient au-dessus de la liste des chantiers, qu'il
// fallait aller chercher quatre écrans plus bas.
const PlanningAlerts = ({ projects, employees, onEditProject, diagnostics }) => {
  const { alerts, late } = diagnostics;
  const [openPanel, setOpenPanel] = useState(null); // 'load' | 'avail' | 'alerts' | 'late'
  const [hours, setHours] = useState('120');
  const [minEmployees, setMinEmployees] = useState('2');
  const [isComplex, setIsComplex] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [availError, setAvailError] = useState(null);
  const [load, setLoad] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Charge réelle du planning : la date du dernier chantier ne dit pas si
  // l'on est plein (elle suit les mois souhaités, et le moteur comble tout
  // temps libre de renforts). Recalculée à chaque changement de données.
  useEffect(() => {
    let cancelled = false;
    projectAPI.planningLoad(6)
      .then((data) => { if (!cancelled) { setLoad(data); setLoadError(null); } })
      .catch(() => { if (!cancelled) setLoadError('Impossible de calculer la charge'); });
    return () => { cancelled = true; };
  }, [projects, employees]);

  const loadAvailability = useCallback(async () => {
    setLoadingAvail(true);
    setAvailError(null);
    try {
      const data = await projectAPI.firstAvailability({
        hours: parseInt(hours, 10) || 0,
        minEmployees: parseInt(minEmployees, 10) || 1,
        isComplex,
      });
      setAvailability(data);
    } catch (e) {
      setAvailError('Impossible de calculer la disponibilité');
    } finally {
      setLoadingAvail(false);
    }
  }, [hours, minEmployees, isComplex]);

  useEffect(() => { loadAvailability(); /* au montage */ // eslint-disable-next-line
  }, []);

  const toggle = (panel) => setOpenPanel((cur) => (cur === panel ? null : panel));

  // --- Contenu des quatre indicateurs ---
  const today = new Date();
  const currentYm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const roomyMonth = load?.months.find((r) => r.month > currentYm && r.capacity && r.free / r.capacity >= 0.25);
  // Le mois le plus en surcapacité : un petit dépassement en fin de mois ne doit pas masquer le gros
  const overloadedMonth = load?.months
    .filter((r) => r.work > r.capacity)
    .sort((a, b) => (b.work - b.capacity) - (a.work - a.capacity))[0];
  const dangerCount = alerts.filter((a) => a.level === 'danger').length;
  const maxLate = late.length ? late[0].days : 0;

  const tiles = [
    {
      key: 'load',
      label: load ? `Heures libres d'ici le ${shortDate(load.bookEnd)}` : 'Charge du planning',
      value: load ? hoursFmt(load.freeUntilBookEnd) : (loadError ? '—' : '…'),
      sub: overloadedMonth
        ? `${monthLabel(overloadedMonth.month)} en surcapacité : ${hoursFmt(overloadedMonth.work)} pour ${hoursFmt(overloadedMonth.capacity)}`
        : roomyMonth ? `${monthLabel(roomyMonth.month)} : ${hoursFmt(roomyMonth.free)} libres`
          : (load ? `sur ${hoursFmt(load.capacityUntilBookEnd)} de capacité` : ''),
      tone: 'border-sky-200 bg-sky-50 text-sky-900',
      subTone: overloadedMonth ? 'text-red-700' : roomyMonth ? 'text-emerald-700' : 'text-sky-700',
    },
    {
      key: 'avail',
      label: `1re dispo · ${parseInt(hours, 10) || 0} h, ${parseInt(minEmployees, 10) || 1} pers.`,
      value: availability?.strict ? shortDate(availability.strict.start) : (availError ? '—' : '…'),
      sub: availability?.flexible && availability.flexible.start !== availability.strict?.start
        ? `${shortDate(availability.flexible.start)} en recalant` : 'sans rien déplacer',
      tone: 'border-green-200 bg-green-50 text-green-900',
      subTone: 'text-green-700',
    },
    {
      key: 'alerts',
      label: 'Alertes du planning',
      value: alerts.length,
      sub: dangerCount ? `dont ${dangerCount} à traiter` : 'rien d\'urgent',
      tone: dangerCount ? 'border-red-200 bg-red-50 text-red-900' : 'border-gray-200 bg-white text-gray-900',
      subTone: dangerCount ? 'text-red-700' : 'text-gray-500',
    },
    {
      key: 'late',
      label: 'Retards sur le mois souhaité',
      value: late.length,
      sub: late.length ? `le plus long +${maxLate} j` : 'aucun',
      tone: late.length ? 'border-orange-200 bg-orange-50 text-orange-900' : 'border-gray-200 bg-white text-gray-900',
      subTone: late.length ? 'text-orange-700' : 'text-gray-500',
    },
  ];

  const slotLine = (slot) =>
    slot
      ? `${fmt(slot.start)} → ${fmt(slot.end)} · ${slot.team.map((t) => t.fullName?.split(' ')[0]).join(', ')}`
      : 'Aucun créneau trouvé sous 12 mois';

  const maxCapacity = load ? Math.max(1, ...load.months.map((r) => r.capacity + (r.shortage || 0))) : 1;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => toggle(t.key)}
            aria-expanded={openPanel === t.key}
            className={`text-left rounded-lg border p-3 transition-shadow hover:shadow-sm ${t.tone} ${
              openPanel === t.key ? 'ring-2 ring-offset-1 ring-sky-300' : ''}`}
          >
            <div className="text-xs opacity-80 truncate">{t.label}</div>
            <div className="text-2xl font-semibold tabular-nums">{t.value}</div>
            <div className={`text-xs truncate ${t.subTone}`}>{t.sub}</div>
          </button>
        ))}
      </div>

      {openPanel === 'load' && (
        <Card className="border-sky-200">
          <CardContent className="p-4 space-y-3">
            {loadError && <p className="text-sm text-red-600">{loadError}</p>}
            {load && (
              <>
                <p className="text-sm text-sky-900">
                  D'ici la fin du carnet ({fmt(load.bookEnd)}) :{' '}
                  <span className="font-semibold">{hoursFmt(load.freeUntilBookEnd)} libres</span>{' '}
                  sur {hoursFmt(load.capacityUntilBookEnd)} de capacité
                  {load.shortageUntilBookEnd > 0 && (
                    <>, et <span className="font-semibold text-red-700">{hoursFmt(load.shortageUntilBookEnd)} sans personne</span> pour les faire</>
                  )}.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="py-2 pr-3 font-medium">Mois</th>
                        <th className="py-2 pr-3 font-medium text-right">Capacité</th>
                        <th className="py-2 pr-3 font-medium text-right">Travail</th>
                        <th className="py-2 pr-3 font-medium text-right">Libre</th>
                        <th className="py-2 pr-3 font-medium text-right">Manque</th>
                        <th className="py-2 font-medium w-1/3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {load.months.map((r) => {
                        const shortage = r.shortage || 0;
                        // Surcapacité : plus de travail (heures sans personne comprises) que de capacité
                        const overloaded = r.work > r.capacity;
                        const scale = r.capacity + shortage;
                        const share = (h) => `${scale ? (h / scale) * 100 : 0}%`;
                        const freeShare = r.capacity ? r.free / r.capacity : 0;
                        return (
                          <tr key={r.month} className={`border-b last:border-0 ${overloaded ? 'bg-red-50' : ''}`}>
                            <td className="py-2 pr-3 text-gray-900">
                              <span className="capitalize">{monthLabel(r.month)}</span>
                              {overloaded && <span className="ml-2 text-xs font-medium text-red-700">surcapacité</span>}
                            </td>
                            <td className="py-2 pr-3 text-right text-gray-600 tabular-nums">{hoursFmt(r.capacity)}</td>
                            <td className={`py-2 pr-3 text-right tabular-nums ${
                              overloaded ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>
                              {hoursFmt(r.work)}
                            </td>
                            <td className={`py-2 pr-3 text-right tabular-nums font-medium ${
                              freeShare >= 0.25 ? 'text-emerald-700' : 'text-gray-500'}`}>
                              {hoursFmt(r.free)}
                            </td>
                            <td className={`py-2 pr-3 text-right tabular-nums font-medium ${
                              overloaded ? 'text-red-700' : shortage > 0 ? 'text-orange-700' : 'text-gray-300'}`}>
                              {shortage > 0 ? hoursFmt(shortage) : '—'}
                            </td>
                            <td className="py-2">
                              <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100"
                                   style={{ width: `${(scale / maxCapacity) * 100}%` }}
                                   title={`${hoursFmt(r.work)} de travail${
                                     shortage > 0 ? ` dont ${hoursFmt(shortage)} sans personne` : ''}, ${hoursFmt(r.free)} libres`}>
                                <div className="bg-sky-500" style={{ width: share(r.covered ?? r.work) }} />
                                <div className="bg-emerald-300" style={{ width: share(r.free) }} />
                                {shortage > 0 && <div className="bg-red-500" style={{ width: share(shortage) }} />}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-500 align-middle mr-1" />travail à faire
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-300 align-middle ml-3 mr-1" />heures libres
                  <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500 align-middle ml-3 mr-1" />heures sans personne
                  {' '}— capacité nette des congés, formations et jours fériés. Les heures libres d'un mois
                  chargé sont surtout la fin de journée perdue quand un chantier se termine. Le travail
                  compte aussi les heures qu'aucun salarié ne couvre avant la fin annoncée des chantiers
                  (le manque) : au-delà de la capacité, le mois est en surcapacité.
                </p>
                {load.uncovered.length > 0 && (
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-1">
                    <div className="text-xs font-medium text-orange-800">
                      Besoin de personnel : heures que personne ne couvre d'ici la fin du chantier
                    </div>
                    {load.uncovered.map((u) => {
                      const project = projects.find((p) => p.id === u.id);
                      return (
                        <div key={u.id}
                             className="text-sm text-orange-900 cursor-pointer hover:underline"
                             onClick={() => project && onEditProject && onEditProject(project)}
                             title="Cliquer pour ouvrir la fiche du chantier">
                          <span className="font-semibold">+{staffNeeded(u, project)} personnel</span>
                          {' '}— {u.name} : {hoursFmt(u.hours)} à couvrir d'ici le {fmt(u.end)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {openPanel === 'avail' && (
        <Card className="border-green-200">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label htmlFor="av-hours" className="text-green-700 text-xs">Heures vendues</Label>
                <Input id="av-hours" type="number" value={hours} onChange={(e) => setHours(e.target.value)}
                       className="w-28 border-green-200" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="av-min" className="text-green-700 text-xs">Salariés min.</Label>
                <Input id="av-min" type="number" value={minEmployees} onChange={(e) => setMinEmployees(e.target.value)}
                       className="w-24 border-green-200" />
              </div>
              <label htmlFor="av-complex" className="flex items-center space-x-2 cursor-pointer pb-2">
                <Checkbox id="av-complex" checked={isComplex} onCheckedChange={setIsComplex} className="border-orange-300" />
                <span className="text-sm text-green-800">Complexe</span>
              </label>
              <Button type="button" onClick={loadAvailability} disabled={loadingAvail}
                      className="bg-green-600 hover:bg-green-700 text-white">
                {loadingAvail ? 'Calcul…' : 'Calculer'}
              </Button>
            </div>
            {availError && <p className="text-sm text-red-600">{availError}</p>}
            {availability && !availError && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                  <div className="text-xs font-medium text-green-800 mb-1">Sans déplacer aucun chantier</div>
                  <div className="text-sm text-green-900">{slotLine(availability.strict)}</div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <div className="text-xs font-medium text-blue-800 mb-1">
                    En recalant les {availability.movableCount} chantier(s) qui ont du mou
                  </div>
                  <div className="text-sm text-blue-900">{slotLine(availability.flexible)}</div>
                  <div className="text-xs text-blue-600 mt-1">
                    Sans pénaliser les {availability.protectedCount} chantier(s) protégés : date imposée, posés à la main,
                    en cours, date de fin exigée, ou déjà dans leur mois souhaité. Les chantiers avancés avant leur
                    mois souhaité peuvent reculer, ils ne bloquent donc pas le créneau.
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {openPanel === 'late' && (
        <Card className="border-orange-200">
          <CardContent className="p-4">
            {late.length === 0 ? (
              <p className="text-sm text-green-700">Aucun retard : tous les chantiers démarrent dans leur mois souhaité (ou avant).</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="py-2 pr-3 font-medium">Chantier</th>
                      <th className="py-2 pr-3 font-medium">Mois souhaité</th>
                      <th className="py-2 pr-3 font-medium">Démarrage prévu</th>
                      <th className="py-2 pr-3 font-medium">Retard</th>
                      <th className="py-2 font-medium">Équipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {late.map(({ project, start, days }) => (
                      <tr key={project.id}
                          className="border-b last:border-0 hover:bg-orange-50 cursor-pointer"
                          onClick={() => onEditProject && onEditProject(project)}
                          title="Cliquer pour ouvrir la fiche du chantier">
                        <td className="py-2 pr-3 font-medium text-gray-900">
                          {project.isUrgent && <Flame className="w-3.5 h-3.5 inline mr-1 text-red-500" />}
                          {project.name}
                        </td>
                        <td className="py-2 pr-3 text-gray-600">{wishLabel(project.startMonth)}</td>
                        <td className="py-2 pr-3 text-gray-900">{fmt(start)}</td>
                        <td className="py-2 pr-3">
                          <Badge className={days > 30 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}>
                            +{days} j
                          </Badge>
                        </td>
                        <td className="py-2 text-gray-600">
                          {(project.assignedEmployees || []).length}/{project.minEmployees}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {openPanel === 'alerts' && (
        <Card className="border-red-200">
          <CardContent className="p-4">
            {alerts.length === 0 ? (
              <p className="text-sm text-green-700">Aucune alerte : le planning est cohérent.</p>
            ) : (
              <div className="space-y-2">
                {alerts.map((a, i) => (
                  <div key={`${a.project.id}-${i}`}
                       className={`flex items-start justify-between gap-3 rounded-lg border p-2.5 cursor-pointer hover:shadow-sm ${levelStyle[a.level]}`}
                       onClick={() => onEditProject && onEditProject(a.project)}
                       title="Cliquer pour ouvrir la fiche du chantier">
                    <div className="flex items-start gap-2 min-w-0">
                      <span className="mt-0.5 shrink-0">{a.icon}</span>
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {a.label} — {a.project.name}
                        </div>
                        <div className="text-xs opacity-80">{a.detail}</div>
                      </div>
                    </div>
                    <div className="text-xs whitespace-nowrap opacity-70">{fmt(a.start)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PlanningAlerts;
